import { useEffect, useRef, useState } from "react";
import { Loader2, ScanLine, Camera, Square, RotateCcw } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCheckin({ token }) {
  const [ticket, setTicket] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [staffName, setStaffName] = useState(() => sessionStorage.getItem("tq_staff_name") || "");
  const scannerRef = useRef(null);

  const stopScan = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      }
    } catch {
      // scanner already stopped
    }
    scannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => () => { stopScan(); }, []);

  const checkin = async (value) => {
    const t = (value || ticket).trim();
    if (!t) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const res = await fetch(`${API}/admin/checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Token": token, "X-Staff-Name": staffName },
        body: JSON.stringify({ token: t }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Check-in failed");
      setResult(data);
      setTicket("");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const startScan = async () => {
    setError("");
    setResult(null);
    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      setScanning(true);
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decoded) => {
          stopScan().then(() => checkin(decoded));
        },
        () => {}
      );
    } catch {
      scannerRef.current = null;
      setScanning(false);
      setError("Camera unavailable — allow camera access, or paste the token below");
    }
  };

  return (
    <div className="mt-8 max-w-lg" data-testid="admin-checkin">
      <p className="font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] mb-2">YOUR NAME (RECORDED WITH EACH SCAN)</p>
      <input
        className="field"
        data-testid="checkin-staff-name"
        placeholder="E.G. RAHUL — GATE 1"
        value={staffName}
        onChange={(e) => {
          setStaffName(e.target.value);
          sessionStorage.setItem("tq_staff_name", e.target.value);
        }}
      />

      <button
        onClick={scanning ? stopScan : startScan}
        data-testid="checkin-scan-btn"
        className="mt-4 w-full min-h-[56px] border border-[#f0eee4] font-mono text-xs tracking-[0.25em] py-4 flex items-center justify-center gap-2 hover:bg-[#f0eee4] hover:text-[#161614] transition-colors"
      >
        {scanning ? <><Square size={14} /> STOP SCANNER</> : <><Camera size={14} /> SCAN WITH CAMERA</>}
      </button>
      <div
        id="qr-reader"
        data-testid="qr-reader"
        className={`mt-4 overflow-hidden border border-[#2e2e2a] bg-black [&_video]:!w-full [&_video]:!object-cover ${scanning ? "" : "hidden"}`}
      />

      <p className="mt-8 font-mono text-[10px] tracking-[0.25em] text-[#6e6e6a] mb-2">OR PASTE TICKET TOKEN</p>
      <div className="flex gap-3">
        <input
          className="field flex-1"
          data-testid="checkin-token"
          placeholder="TICKET TOKEN"
          value={ticket}
          onChange={(e) => setTicket(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && checkin()}
        />
        <button
          onClick={() => checkin()}
          disabled={busy || !ticket.trim()}
          data-testid="checkin-submit"
          className="btn-solid bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.2em] px-6 py-3 hover:bg-white disabled:opacity-40 inline-flex items-center gap-2"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={14} />} CHECK IN
        </button>
      </div>

      {result && (
        <div className="mt-6 border border-[#f0eee4] bg-[#1e1e1c] p-6" data-testid="checkin-success">
          <p className="font-display font-extrabold uppercase text-3xl">CHECKED IN.</p>
          <div className="mt-4 font-mono text-[11px] tracking-[0.15em] space-y-2">
            <div className="flex justify-between"><span className="text-[#6e6e6a]">ATHLETE</span><span data-testid="checkin-name">{result.name}</span></div>
            <div className="flex justify-between"><span className="text-[#6e6e6a]">CATEGORY</span><span>{result.category}</span></div>
            <div className="flex justify-between"><span className="text-[#6e6e6a]">BOOKING</span><span>{result.booking_ref}</span></div>
          </div>
          <button
            onClick={startScan}
            data-testid="checkin-scan-next"
            className="mt-6 w-full btn-solid bg-[#ff5a1f] text-[#0c0c0a] font-mono text-xs tracking-[0.25em] py-4 min-h-[52px] hover:bg-white inline-flex items-center justify-center gap-2"
          >
            <RotateCcw size={14} /> SCAN NEXT
          </button>
        </div>
      )}
      {error && (
        <p className="mt-6 font-mono text-[11px] tracking-[0.15em] text-[#f0eee4] border border-[#454540] px-4 py-3" data-testid="checkin-error">
          {error}
        </p>
      )}
    </div>
  );
}
